import { ASSET_KIND_LABELS, type MaintenancePlan } from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Label,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarClock, Plus, RefreshCw } from "lucide-react";
import { type ReactElement, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  planDueText,
  planIntervalText,
  planProgress,
} from "../../../features/maintenance/maintenance-values.js";
import { PlanDialog } from "../../../features/maintenance/plan-dialog.js";
import {
  useMaintenancePermissions,
  useMaintenancePlans,
  useRefreshMaintenance,
} from "../../../features/maintenance/queries.js";
import { MaintenancePage, PriorityBadge, StationFilterField } from "../../../features/maintenance/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  due: z.enum(["1"]).optional().catch(undefined),
  planId: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/bakim/planlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PlansRoute,
});

/**
 * Bakım planları (Prompt 23 web, `GET /maintenance/plans`): aralık (N gün / saat / adet), vade ve ilerleme, açık iş emri.
 * Yönetici plan kurar / düzenler ve "şimdi değerlendir" ile vadesi gelen plana iş emri açtırır (worker'ın günlük işiyle
 * aynı işlev; idempotent — açık iş emri varken ikincisi açılmaz).
 */
function PlansRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMaintenance();
  const permissions = useMaintenancePermissions();
  const search = Route.useSearch();
  const [editing, setEditing] = useState<MaintenancePlan | "new" | null>(null);
  const evaluate = useMutation({
    mutationFn: async () => (await api.POST("/maintenance/plans/evaluate")).data!,
    onSuccess: async (result) => {
      await refresh();
      toast({
        tone: "success",
        title: t("maintenance.plans.evaluated", {
          count: result.created.length,
          evaluated: result.evaluated,
        }),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  return (
    <MaintenancePage
      title={t("menu.maintenancePlans")}
      description={t("maintenance.plans.description")}
      actions={
        permissions.canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" loading={evaluate.isPending} onClick={() => evaluate.mutate()}>
              <RefreshCw aria-hidden="true" />
              {t("maintenance.plans.evaluate")}
            </Button>
            <Button onClick={() => setEditing("new")}>
              <Plus aria-hidden="true" />
              {t("maintenance.plans.new")}
            </Button>
          </div>
        ) : null
      }
    >
      <PlanList onEdit={permissions.canManage ? setEditing : null} />
      {editing ? (
        <PlanDialog
          open
          onOpenChange={() => setEditing(null)}
          plan={editing === "new" ? null : editing}
          defaultStationId={search.station ?? null}
        />
      ) : null}
    </MaintenancePage>
  );
}

function PlanList({ onEdit }: { onEdit: ((plan: MaintenancePlan) => void) | null }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const dueId = useId();
  const plans = useMaintenancePlans({ stationId: search.station ?? null, due: search.due === "1" });
  const setSearch = (next: { station?: string | null; due?: boolean }) => {
    const station = next.station !== undefined ? next.station : (search.station ?? null);
    const due = next.due !== undefined ? next.due : search.due === "1";
    void navigate({
      to: ".",
      search: { ...(station ? { station } : {}), ...(due ? { due: "1" } : {}) } as never,
      replace: true,
    });
  };

  const columns = useMemo<DataTableColumn<MaintenancePlan>[]>(
    () => [
      {
        id: "plan",
        header: t("maintenance.fields.plan"),
        hideable: false,
        cell: (row) => (
          <span
            className={`grid min-w-56 gap-0.5 ${row.id === search.planId ? "rounded bg-warning-subtle p-1" : ""}`}
          >
            <span className="font-medium">{row.name}</span>
            <Link
              to="/bakim/varliklar/$kind/$id"
              params={{ kind: row.assetKind, id: row.assetId }}
              className="text-xs text-muted-foreground underline underline-offset-4"
            >
              {ASSET_KIND_LABELS[row.assetKind]} · {row.assetName}
            </Link>
          </span>
        ),
      },
      { id: "station", header: t("maintenance.fields.station"), cell: (row) => row.stationCode },
      { id: "interval", header: t("maintenance.fields.interval"), cell: (row) => planIntervalText(row) },
      {
        id: "due",
        header: t("maintenance.fields.due"),
        cell: (row) => (
          <span className="grid min-w-44 gap-1">
            <span className="text-sm">{planDueText(row, formatDate)}</span>
            <span
              className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label={t("maintenance.plans.progress", { name: row.name })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={planProgress(row)}
            >
              <span
                className={`block h-full ${row.due.due ? "bg-warning" : "bg-primary"}`}
                style={{ width: `${planProgress(row)}%` }}
              />
            </span>
          </span>
        ),
      },
      {
        id: "state",
        header: t("maintenance.fields.status"),
        cell: (row) =>
          !row.isActive ? (
            <StatusBadge>{t("common.inactive")}</StatusBadge>
          ) : row.openWorkOrder ? (
            <Link
              to="/bakim/is-emirleri/$id"
              params={{ id: row.openWorkOrder.id }}
              className="text-sm underline underline-offset-4"
            >
              {row.openWorkOrder.workOrderNo}
            </Link>
          ) : row.due.due ? (
            <StatusBadge tone="warning">{t("maintenance.plans.dueNow")}</StatusBadge>
          ) : (
            <StatusBadge tone="success">{t("maintenance.plans.onTrack")}</StatusBadge>
          ),
      },
      {
        id: "priority",
        header: t("maintenance.fields.priority"),
        cell: (row) => <PriorityBadge priority={row.priority} />,
      },
      {
        id: "lastCompleted",
        header: t("maintenance.fields.lastCompletedOn"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDate(row.lastCompletedOn)}</span>,
      },
      ...(onEdit
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              hideable: false,
              cell: (row: MaintenancePlan) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(row)}
                  aria-label={t("maintenance.plans.editNamed", { name: row.name })}
                >
                  {t("common.edit")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, onEdit, search.planId],
  );

  return (
    <DataTable
      caption={t("menu.maintenancePlans")}
      columns={columns}
      rows={plans.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={plans.isPending}
      error={plans.error ? <Alert tone="danger">{errorMessage(plans.error)}</Alert> : undefined}
      filters={
        <>
          <StationFilterField value={search.station ?? null} onChange={(station) => setSearch({ station })} />
          <div className="flex h-10 items-center gap-2">
            <Checkbox
              id={dueId}
              checked={search.due === "1"}
              onChange={(event) => setSearch({ due: event.target.checked })}
            />
            <Label htmlFor={dueId}>{t("maintenance.plans.dueOnly")}</Label>
          </div>
        </>
      }
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<CalendarClock />}
          title={t("maintenance.plans.emptyTitle")}
          description={t("maintenance.plans.emptyDescription")}
        />
      }
    />
  );
}
